import React, { useState, useCallback } from 'react';
import { Chip } from '@mui/material';

// Shared Autocomplete behavior for the checkbox multi-select filters
// (MultiSelectFilter, DependentMultiSelectPairFilter).

// A partial selection larger than this collapses into one summary chip.
// Rendering one chip per value buries the search input under hundreds of
// chips as soon as the user deselects a single value from "All".
export const MAX_INDIVIDUAL_TAGS = 3;

/**
 * Controlled search text for a multi-select Autocomplete that survives
 * toggling options.
 *
 * MUI clears the input every time an option is picked in `multiple` mode
 * (reason 'reset'), which throws the user back to the full, unfiltered
 * list after each checkbox. Like Excel's filter search, the text should
 * stay until the dropdown closes, so the user can check several matches
 * or refine the search on top of an existing selection.
 *
 * All returned callbacks have empty deps (stable identity) — see the
 * filter-component-stability skill.
 */
export function useRetainedSearchInput() {
  const [inputValue, setInputValue] = useState('');

  const onInputChange = useCallback((event, value, reason) => {
    if (reason === 'reset') return;
    setInputValue(value);
  }, []);

  const onClose = useCallback(() => {
    setInputValue('');
  }, []);

  return { inputValue, onInputChange, onClose };
}

/**
 * Chip rendering for a multi-select filter's current selection.
 *
 * - Everything selected → one "All (N)" chip.
 * - Up to MAX_INDIVIDUAL_TAGS selected → one deletable chip per value.
 * - More than that → one "N of M selected" chip.
 *
 * Summary chips are not deletable: MUI's getTagProps delete would remove
 * whichever single value sits at index 0, which is never what "delete All"
 * or "delete 12 of 40" means. The Autocomplete's clear button and the
 * "Select All" option remain the ways to bulk-change the selection.
 */
export function renderSelectionTags(value, getTagProps, { totalCount, getLabel }) {
  const selectedCount = value.length;

  const renderSummaryChip = (label) => {
    // eslint-disable-next-line no-unused-vars
    const { onDelete, key, ...tagProps } = getTagProps({ index: 0 });
    return <Chip {...tagProps} key="summary-chip" label={label} size="small" />;
  };

  if (selectedCount === totalCount && totalCount > 0) {
    return renderSummaryChip(`All (${totalCount})`);
  }

  if (selectedCount > MAX_INDIVIDUAL_TAGS) {
    return renderSummaryChip(`${selectedCount} of ${totalCount} selected`);
  }

  return value.map((option, index) => {
    const label = getLabel(option);
    const { key, ...tagProps } = getTagProps({ index });
    return <Chip {...tagProps} key={label} label={label} size="small" />;
  });
}
